import React from 'react';
import { Chip, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { IProductHolder } from '../../Services/productEntitlements';
import { IOverage } from './useProductOverages';

interface Props {
	holders: IProductHolder[];
	/** Dépassements calculés à la demande, par compte. */
	overages?: Record<string, IOverage> | null;
	overageLabel?: string;
}

const eur = (cents: number) => `${(cents / 100).toFixed(2)} €`;

/** Ce que le compte a de remarquable sur ce produit — sinon rien. */
const state = (h: IProductHolder): { label: string; color: 'error' | 'warning' | 'default' } | null => {
	// ⚠️ « Sans droit » ne vaut que pour une ligne qui PLAFONNE : à quantité 0,
	// une ligne « Illimité » ouvre bien le produit. L'alerte rouge y désignait
	// comme fraude un contrat parfaitement en règle.
	if (h.blocking && h.quantity === 0 && h.usage > 0) return { label: 'consomme sans droit', color: 'error' };
	// ⚠️ Une ligne « Illimité » (contrat, !blocking) ne PEUT PAS être au-delà de
	// son droit : sa quantité est indicative. L'alerte orange y contredisait la
	// mention « · illimité » de la même ligne, et se lisait comme « la case n'a
	// pas pris ». L'écart reste visible dans la colonne Usage.
	if (!h.blocking) return null;
	if (h.quantity > 0 && h.usage > h.quantity) return { label: 'au-delà du droit', color: 'warning' };
	return null;
};

export const ProductHoldersTable: React.FC<Props> = ({ holders, overages, overageLabel }) => (
	<Table size="small">
		<TableHead>
			<TableRow>
				<TableCell>Compte</TableCell>
				<TableCell>Facturation</TableCell>
				<TableCell align="right">
					<Tooltip title="Le droit posé : quantité de l'abonnement Stripe, ou du contrat.">
						<span>Droit</span>
					</Tooltip>
				</TableCell>
				<TableCell align="right">Usage</TableCell>
				<TableCell>État</TableCell>
				<TableCell>Source</TableCell>
				{overages !== undefined && <TableCell align="right">{overageLabel ?? 'Dépassement'}</TableCell>}
			</TableRow>
		</TableHead>
		<TableBody>
			{holders.map((h) => {
				const st = state(h);
				const amount = overages?.[h.billingAccountId];
				return (
					<TableRow key={h.billingAccountId}>
						<TableCell>
							<Link to={`/billing-accounts/${h.billingAccountId}`}>{h.name || h.organisationName || h.billingAccountId}</Link>
							{h.organisationName && h.organisationName !== h.name && (
								<Typography variant="caption" display="block" color="text.secondary">
									{h.organisationName}
								</Typography>
							)}
						</TableCell>
						<TableCell>
							{h.billingMode === 'contract' ? (
								<Chip size="small" color="primary" label="Entreprise" />
							) : (
								<Chip size="small" label="Pay as you go" />
							)}
						</TableCell>
						<TableCell align="right">{h.quantity > 0 ? h.quantity : h.blocking ? '—' : '∞'}</TableCell>
						<TableCell align="right">{h.usage}</TableCell>
						<TableCell>{st ? <Chip size="small" color={st.color} label={st.label} /> : ''}</TableCell>
						<TableCell>
							<Typography variant="caption">
								{h.source === 'admin' ? 'Contrat' : 'Stripe'}
								{h.source === 'admin' && !h.blocking ? ' · illimité' : ''}
							</Typography>
						</TableCell>
						{overages !== undefined && (
							<TableCell align="right">
								{overages === null || amount === undefined ? (
									overages === null ? '—' : '…'
								) : amount.cents > 0 ? (
									// Le libellé porte les volumes : c'est lui qui permet de recetter
									// le montant sans rouvrir la facture Stripe.
									<Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{amount.labels.join('\n')}</span>}>
										<strong>{eur(amount.cents)}</strong>
									</Tooltip>
								) : (
									eur(0)
								)}
							</TableCell>
						)}
					</TableRow>
				);
			})}
		</TableBody>
	</Table>
);
